// tslint:disable: completed-docs

import { createSelector, Selector } from 'reselect';
import { CONTAINER_OPTIONS } from 'Constant';
import { initialState } from './reducer';
import findIndex from 'Helper/findIndex';
import {
    CAPACITY_KEY,
    COLOR_KEY,
} from './constants';
import { selectDevice } from '../DeviceDetail/selectors';
import arrayToHashmap from 'Helper/arrayToHashmap';
import merge from 'lodash.merge';
import { IInitialState as IInitialStateOptions } from './interfaces/state';
import {
    IInitialState as IInitialStateDeviceDetail,
} from '../DeviceDetail/interfaces/state';
import {
    Atomic,
    Capacity,
    Color,
    TechnicalDetail,
} from '../DeviceDetail/interfaces/api';
import { RootState } from '@vfde-fe/sails_core';

const selectOptionsContainerState = (state: RootState<IInitialStateOptions>) => state[CONTAINER_OPTIONS] || initialState;

const selectAtomicId = (): Selector<RootState<IInitialStateOptions>, IInitialStateOptions['atomicId']> =>
    createSelector(
        selectOptionsContainerState,
        state => state.atomicId,
    );

const selectCurrentColor = (): Selector<RootState<IInitialStateOptions>, IInitialStateOptions['currentColor']> =>
    createSelector(
        selectOptionsContainerState,
        state => state.currentColor,
    );

const selectCurrentCapacity = (): Selector<RootState<IInitialStateOptions>, number> =>
    createSelector(
        selectOptionsContainerState,
        state => Number(state.currentCapacity),
    );

const selectColors = (): Selector<RootState<IInitialStateDeviceDetail>, Color[] | undefined> =>
    createSelector(
        selectDevice(),
        device => device?.colors,
    );

const selectCapacities = (): Selector<RootState<IInitialStateDeviceDetail>, Capacity[] | undefined> =>
    createSelector(
        selectDevice(),
        device => device?.capacities,
    );

const selectDefaultCapacity = (): Selector<RootState<IInitialStateDeviceDetail>, Capacity | undefined> =>
    createSelector(
        selectCapacities(),
        capacities => capacities?.filter(c => c.default)[0],
    );

const selectDefaultSize = (): Selector<RootState<IInitialStateDeviceDetail>, number | undefined> =>
    createSelector(
        selectDefaultCapacity(),
        defaultCapacity => defaultCapacity?.[CAPACITY_KEY],
    );

const selectForceRerender = (): Selector<RootState<IInitialStateOptions>, boolean> =>
    createSelector(
        selectOptionsContainerState,
        state => state.forceRerender,
    );

const selectImages = (): Selector<RootState<IInitialStateDeviceDetail & IInitialStateOptions>, Atomic['images'] | undefined> =>
    createSelector(
        selectColors(), selectCurrentColor(),
        (colors, color) => {
            if (!color) {
                return [];
            }
            const index = findIndex(colors!, COLOR_KEY, color);
            const atomicDevice = colors?.[index].capacities[0];

            return atomicDevice?.images;
        },
    );

const selectCapacitiesForColor = (): Selector<RootState<IInitialStateDeviceDetail & IInitialStateOptions>, Atomic[] | undefined> =>
    createSelector(
        selectColors(), selectCurrentColor(),
        (colors, color) => {
            if (!color) {
                return [];
            }
            const colorIndex = findIndex(colors!, COLOR_KEY, color);
            const filteredCapacities = colors?.[colorIndex].capacities.filter(c => c.addon?.available === false);

            return filteredCapacities;
        },
    );

const selectSizesForColor = (): Selector<RootState<IInitialStateDeviceDetail & IInitialStateOptions>, number[] | null> =>
    createSelector(
        selectCapacitiesForColor(),
        capacities => {
            // @TODO: We have to remove duplicate sizes manually till addon data is removed from API
            const allSizes = capacities ? capacities.map(capacity => capacity[CAPACITY_KEY]) : [];
            const uniqSizes = allSizes.length ? [...new Set(allSizes)] : null;

            return uniqSizes;
        },
    );

const selectAtomicDevice = (): Selector<RootState<IInitialStateDeviceDetail & IInitialStateOptions>, Atomic | undefined> =>
    createSelector(
        selectDevice(), selectCurrentColor(), selectCurrentCapacity(),
        (device, color, capacity) => {
            if (!color || !capacity) {
                return;
            }
            const filteredColor = device?.colors.filter(c => c.name === color)[0];
            const filteredCapacity = filteredColor?.capacities.filter(c => c[CAPACITY_KEY] === capacity);

            return filteredCapacity?.filter(c => c.addon?.available === false)[0];
        },
    );

const selectTechnicalDetails = (): Selector<RootState<IInitialStateDeviceDetail & IInitialStateOptions>, { [key: string]: TechnicalDetail; } | null> =>
    createSelector(
        selectDevice(), selectAtomicDevice(),
        (device, atomic) => atomic ? merge(
            arrayToHashmap(merge([], device?.technicalDetails), ['section', 'attribute'], true),
            arrayToHashmap(merge([], atomic.technicalDetails) || [], ['section', 'attribute'], true),
        ) : null,
    );

const selectDeliveryScope = (): Selector<RootState<IInitialStateDeviceDetail & IInitialStateOptions>, Atomic['deliveryScope'] | null> =>
    createSelector(
        selectAtomicDevice(),
        atomic => atomic ? atomic.deliveryScope : null,
    );

const selectPrice = (): Selector<RootState<IInitialStateDeviceDetail & IInitialStateOptions>, Pick<Atomic, 'oneTimePrice' | 'totalPrice'> | null> =>
    createSelector(
        selectAtomicDevice(),
        atomic => atomic ? (({oneTimePrice, totalPrice}) => ({oneTimePrice, totalPrice}))(atomic) : null,
    );

const selectDeliveryDate = (): Selector<RootState<IInitialStateDeviceDetail & IInitialStateOptions>, Pick<Atomic, 'shippingDate' | 'hwOnlyShippingDate'> | null> =>
    createSelector(
        selectAtomicDevice(),
        atomic => atomic ? (({shippingDate, hwOnlyShippingDate}) => ({shippingDate, hwOnlyShippingDate}))(atomic) : null,
    );

const selectOptionsByAtomicId = (atomicId: number): Selector<RootState<IInitialStateDeviceDetail>, { color: Color['name'] | null, capacity: Atomic['sortSize'] | null }> =>
    createSelector(
        selectColors(),
        (colors) => {
            const colorObj = colors?.filter(color => color.capacities.filter(c => c.atomicId === atomicId)[0])[0];
            const capacityObj = colorObj ? colorObj.capacities.filter(c => c.atomicId === atomicId)[0] : null;

            const color = colorObj ? colorObj.name : null;
            const capacity = capacityObj ? capacityObj[CAPACITY_KEY] : null;

            return { color, capacity };
        },
    );

const selectCellular = ():  Selector<RootState<IInitialStateDeviceDetail & IInitialStateOptions>, Atomic['cellular'] | undefined> =>
    createSelector(
        selectAtomicDevice(),
        device => device?.cellular,
    );

export {
    selectAtomicId,
    selectCapacities,
    selectDefaultCapacity,
    selectDefaultSize,
    selectCurrentColor,
    selectCurrentCapacity,
    selectColors,
    selectCapacitiesForColor,
    selectSizesForColor,
    selectImages,
    selectPrice,
    selectTechnicalDetails,
    selectDeliveryScope,
    selectDeliveryDate,
    selectAtomicDevice,
    selectOptionsByAtomicId,
    selectForceRerender,
    selectCellular,
};
